/* eslint-disable camelcase */

import { capitalize } from 'lodash';
import moment from 'moment';
import React from 'react';
import type { User } from 'types';
import { userRolesToTypes } from 'utils/userRoles';

type ComponentProps = {
  user: User;
  selectedUsers: User[];
  handleSelectUser: (event: React.ChangeEvent<HTMLInputElement>, user: User) => void;
};

const AllUserTableRow: React.FC<ComponentProps> = ({ user, selectedUsers, handleSelectUser }) => {
  const renderTeamNames = (): string => {
    if (user.teams && user.teams.length > 0) {
      return (
        user.teams
          // @ts-ignore
          .filter((t) => t.kind === 'division')
          .reduce(
            (acc, t, index, arr) => `${acc}${t.name}${index !== arr.length - 1 ? ', ' : ''}`,
            ''
          )
      );
    }
    return '–';
  };

  const renderRegionNames = (): string => {
    if (user.teams && user.teams.length > 0) {
      return (
        user.teams
          // @ts-ignore
          .filter((t) => t.kind === 'region')
          .reduce(
            (acc, t, index, arr) => `${acc}${t.name}${index !== arr.length - 1 ? ', ' : ''}`,
            ''
          )
      );
    }
    return '–';
  };

  const renderManagementLevelNames = (): string => {
    if (!user.teams?.length) return '';

    const levels = user.teams
      // @ts-ignore
      .filter((t) => t.kind === 'management-level')
      .map((t) => t.name);

    if (!levels.length) return '';

    return `(${levels.join(', ')})`;
  };

  const getHasIntegrations = (renderedUser: User, mode: 'prevent' | 'insight'): boolean => {
    const { insight_active_chat, insight_active_email, prevent_active_chat, prevent_active_email } =
      renderedUser;

    if (mode === 'prevent' && (prevent_active_chat || prevent_active_email)) {
      return true;
    }

    if (mode === 'insight' && (insight_active_chat || insight_active_email)) {
      return true;
    }

    return false;
  };

  const renderLastMessage = (renderedUser: User, mode: 'prevent' | 'insight'): JSX.Element => {
    if (!renderedUser.ms_graph_active) {
      return <span className="text-litlingo-alert">Azure permissions</span>;
    }

    if (!getHasIntegrations(renderedUser, mode)) {
      return <span>–</span>;
    }

    const {
      recent_chat_communication: lastChat,
      recent_email_communication: lastInsigthEmail,
      // @ts-ignore
      recent_email_app_communication: lastPreventEmail,
    } = renderedUser;

    if (!lastChat && !lastInsigthEmail && !lastPreventEmail) {
      return <span>–</span>;
    }

    const daysFromNowInsightChat = moment().diff(moment(lastChat), 'days');
    const daysFromNowPreventChat = moment().diff(moment(lastChat), 'days');

    const daysFromNowInsightEmail = moment().diff(moment(lastInsigthEmail), 'days');
    const daysFromNowPreventEmail = moment().diff(moment(lastPreventEmail), 'days');

    let lastInsightChatTag: JSX.Element | null = null;
    let lastPreventChatTag: JSX.Element | null = null;
    let lastInsightEmailTag: JSX.Element | null = null;
    let lastPreventEmailTag: JSX.Element | null = null;

    const lastInsightChatTimeFrom = lastChat ? moment(lastChat).fromNow() : '';
    const lastPreventChatTimeFrom = lastChat ? moment(lastChat).fromNow() : '';
    const lastInsightEmailTimeFrom = lastInsigthEmail ? moment(lastInsigthEmail).fromNow() : '';
    const lastPreventEmailTimeFrom = lastPreventEmail ? moment(lastPreventEmail).fromNow() : '';

    if (daysFromNowInsightChat > 14) {
      lastInsightChatTag = <span className="text-litlingo-alert">Chat: 2+ weeks</span>;
    } else {
      lastInsightChatTag = lastChat ? <span>{`Chat: ${lastInsightChatTimeFrom}`}</span> : <></>;
    }

    if (daysFromNowPreventChat > 14) {
      lastPreventChatTag = <span className="text-litlingo-alert">Chat: 2+ weeks</span>;
    } else {
      lastPreventChatTag = lastChat ? <span>{`Chat: ${lastPreventChatTimeFrom}`}</span> : <></>;
    }

    if (daysFromNowInsightEmail > 14) {
      lastInsightEmailTag = <span className="text-litlingo-alert">Email: 2+ weeks</span>;
    } else {
      lastInsightEmailTag = lastInsigthEmail ? (
        <span>{`Email: ${lastInsightEmailTimeFrom || 'n/a'}`}</span>
      ) : (
        <></>
      );
    }

    if (daysFromNowPreventEmail > 14) {
      lastPreventEmailTag = <span className="text-litlingo-alert">Email: 2+ weeks</span>;
    } else {
      lastPreventEmailTag = lastPreventEmail ? (
        <span>{`Email: ${lastPreventEmailTimeFrom || 'n/a'}`}</span>
      ) : (
        <></>
      );
    }

    if (mode === 'insight') {
      return (
        <>
          {lastInsightEmailTag}
          {lastInsightChatTag}
        </>
      );
    }

    return (
      <>
        {lastPreventEmailTag}
        {lastPreventChatTag}
      </>
    );
  };

  const selected = !!selectedUsers.find((u) => u.uuid === user.uuid);

  return (
    <tr key={user.uuid} className="table-wrapper__new-tr table-row align-top">
      <td className="table-wrapper__new-td">
        <div className="table-wrapper__new-cell-content flex h-auto">
          <input
            id={`checkbox-user-${user.uuid}`}
            data-testid="select-button"
            type="checkbox"
            className="cursor-pointer form-checkbox litlingo-checkbox w-5 h-5"
            onClick={(e): void => e.stopPropagation()}
            onChange={(e): void => {
              handleSelectUser(e, user);
            }}
            checked={selected}
          />
        </div>
      </td>

      <td className="table-wrapper__new-td">
        <div className="table-wrapper__new-cell-content min-h-14 h-auto pr-4 flex flex-col text-body">
          <div className="w-full flex flex-row justify-between">
            <span className="truncate">
              {user.name} {renderManagementLevelNames()}
            </span>
          </div>
          <div className="w-full truncate">{user.email}</div>
        </div>
      </td>
      <td className="table-wrapper__new-td">
        <div className="table-wrapper__new-cell-content min-h-5 h-auto pr-4 flex items-center">
          <div className="text-body clamped-text-2 overflow-hidden">
            {userRolesToTypes(user.roles) !== null && userRolesToTypes(user.roles).length > 0
              ? userRolesToTypes(user.roles).map((item, index) => {
                  if (index === 0) {
                    return capitalize(item.label);
                  }
                  return `, ${capitalize(item.label)}`;
                })
              : '–'}
          </div>
        </div>
      </td>
      <td className="table-wrapper__new-td">
        <div className="table-wrapper__new-cell-content min-h-14 h-auto pr-4 flex items-center">
          <div className="text-left text-body clamped-text-2 overflow-hidden">
            {renderTeamNames()}
          </div>
        </div>
      </td>
      <td className="table-wrapper__new-td">
        <div className="table-wrapper__new-cell-content min-h-14 h-auto pr-4 flex items-center">
          <div className="text-left text-body clamped-text-2 overflow-hidden">
            {renderRegionNames()}
          </div>
        </div>
      </td>
      <td className="table-wrapper__new-td">
        <div className="table-wrapper__new-cell-content min-h-14 h-auto pr-4 flex flex-col justify-center items-start text-body">
          {renderLastMessage(user, 'prevent')}
        </div>
      </td>
      <td className="table-wrapper__new-td">
        <div className="table-wrapper__new-cell-content min-h-14 h-auto pr-4 flex flex-col justify-center items-start text-body">
          {renderLastMessage(user, 'insight')}
        </div>
      </td>
    </tr>
  );
};

export default AllUserTableRow;
